import * as PIXI from "pixi.js";
import { environment } from "../../../../environments/environment";
import { cardDecoder } from "../../../shared/helpers/card-decoder";
import { PreBetType, Table } from "./table";
import { CurrencyInfo } from "../../../shared/models/currency-info";
import { VariantType2 } from "../../../shared/enums/poker-types";

// Enable: localStorage.setItem('mockFullTable', 'true') or ?mockFullTable=true before the # in the URL. Disable: localStorage.removeItem('mockFullTable'). Reload after changing.
function isMockFullTableRequested(): boolean {
  if (environment.mockFullTable === true) {
    return true;
  }
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    if (params.get("mockFullTable") === "true") {
      return true;
    }
  }
  return (
    typeof localStorage !== "undefined" &&
    localStorage.getItem("mockFullTable") === "true"
  );
}

export function mockFullTable(table: Table) {
  const requested = isMockFullTableRequested();
  console.log("@mockFullTable called", {
    production: environment.production,
    requested,
    urlSearch: typeof window !== "undefined" ? window.location.search : "n/a",
    hasLocalStorage: typeof localStorage !== "undefined",
    flagValue:
      typeof localStorage !== "undefined"
        ? localStorage.getItem("mockFullTable")
        : "n/a",
  });
  if (environment.production) {
    return;
  }
  if (!requested) {
    return;
  }

  try {
    mockFullTableUnsafe(table);
  } catch (error) {
    console.error("@mockFullTable failed", error);
  }
}

const MOCK_PRE_BET_INSTEAD_OF_ACTIONS = false;
const MOCK_RABBIT_HUNTING = true;
const MOCK_RABBIT_HUNTING_CARDS = true;
const MOCK_HIDE_OPPONENT_CARDS = false;
const MOCK_PLAYER_CARD_COUNT = 5;
const MOCK_SHOW_WINNER_BANNER = false;
// Seven Stud hands: 7 cards, the 3rd to 6th are face up for everyone
const MOCK_SEVEN_STUD = true;
const MOCK_BOARD: "single" | "double" | "r2t-river" | "r2t-turn-river" =
  "double";

const CARD_SUITS = [0, 1, 2, 3];
const CARD_NUMBERS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

function mockFullTableUnsafe(table: Table) {
  const maxPlayers = table.getMaxPlayers();
  console.log(`@mockFullTable filling ${maxPlayers} seats`);

  const dealerSeat = 0;
  const betAmounts = [
    24.4, 2.75, 0.05, 1234.56, 0.5, 37.85, 5, 189.99, 0.15, 12.6, 2500,
    0.85, 66.66, 1, 350.25, 7.3, 15000, 0.3, 99.95, 3.5, 555.55, 0.1,
  ];

  const mockCurrency: CurrencyInfo = {
    Code: "",
    Id: 0,
    IsVisibleToClient: true,
    Iso: "",
    Multiplier: 10000,
    Name: "",
    NbDecimalVisible: 2,
    Symbol: "$",
    SymbolAfter: false,
  };

  const originalCurrency = table.currency;
  table.currency = mockCurrency;

  if (MOCK_SEVEN_STUD) {
    table.getVariant = () => VariantType2.SevenStud;
  }
  const playerCardCount = MOCK_SEVEN_STUD ? 7 : MOCK_PLAYER_CARD_COUNT;

  const topSeat = Math.floor(maxPlayers / 2);
  for (let i = 0; i < maxPlayers; i++) {
    const id = -(i + 1);
    const betAmount =
      (i === topSeat ? 2.75 : betAmounts[i % betAmounts.length]) * 10000;

    const cards = [];
    for (let c = 0; c < playerCardCount; c++) {
      const isStudPublic = c >= 2 && c <= 5;
      cards.push({
        Suit: CARD_SUITS[c % CARD_SUITS.length],
        Number: CARD_NUMBERS[(i + c) % CARD_NUMBERS.length],
        IsHidden: MOCK_SEVEN_STUD
          ? !isStudPublic && i !== dealerSeat
          : MOCK_HIDE_OPPONENT_CARDS && i !== dealerSeat,
        IsRabbitHunting: false,
        IsPublic: MOCK_SEVEN_STUD ? isStudPublic : true,
      });
    }

    const player = table.addPlayer(i, {
      Id: id,
      Name: `Mock ${i + 1}`,
      Avatar: "",
      Money: 10000,
      Status: 0,
      Country: "",
      City: "",
      TimeBank: 0,
      RunItTwice: false,
      Bounty: 0,
      BountyEarned: 0,
      MoneyBid: betAmount,
      Cards: cards,
      PostedStraddle: false,
    });

    table.addPlayerPot(i, id, betAmount);

    if (i === dealerSeat) {
      table.setDealer(id);
      table.currentPlayer = player;
    }
  }

  table.currency = originalCurrency;

  mockDealerButtonsAtEverySeat(table, maxPlayers);

  const board = [
    { Suit: 1, Number: 8 },
    { Suit: 1, Number: 10 },
    { Suit: 0, Number: 3 },
    { Suit: 1, Number: 9 },
    { Suit: 3, Number: 6 },
  ];
  const secondBoard = [
    { Suit: 0, Number: 11 },
    { Suit: 1, Number: 6 },
    { Suit: 2, Number: 5 },
    { Suit: 2, Number: 13 },
    { Suit: 2, Number: 3 },
  ];
  const sharedCards = { single: 5, double: 0, "r2t-river": 4, "r2t-turn-river": 3 }[MOCK_BOARD];
  const withRabbitHunting = (cards: { Suit: number; Number: number }[]) =>
    cards.map((card) => ({ ...card, IsRabbitHunting: MOCK_RABBIT_HUNTING_CARDS }));
  table.communityCards(
    withRabbitHunting(board.slice(0, sharedCards)),
    MOCK_BOARD === "single" ? null : withRabbitHunting(board.slice(sharedCards)),
    MOCK_BOARD === "single" ? null : withRabbitHunting(secondBoard.slice(sharedCards)),
  );

  const potMultiplier = table.currency?.Multiplier ?? 1;
  if (MOCK_BOARD === "single") {
    table.mainPot?.updateAmount(153.7 * potMultiplier);
  } else {
    table.mainPot?.updateAmount(76.85 * potMultiplier);
    table.mainPot2?.updateAmount(76.85 * potMultiplier);
  }
  table.potsCalculateTotalValueOnTable();

  mockBetArea(table);
  mockTableActionButtons(table);
  setInterval(() => {
    mockBetArea(table);
    mockTableActionButtons(table);
  }, 500);

  if (MOCK_SHOW_WINNER_BANNER) {
    setTimeout(() => {
      table.animationText.setConfig(
        "Mock 1 won with a ",
        "Full House",
        24 * 60 * 60 * 1000,
        table.useVerticalMode,
      );
    }, 100);
  }
}

function toTableCardsData(cards: { Suit: number; Number: number }[]) {
  return cards.map((card) => ({
    Suit: card.Suit,
    Number: card.Number,
    Name: cardDecoder(card.Suit, card.Number),
    IsRabbitHunting: false,
    IsPublic: true,
  }));
}

function mockBetArea(table: Table) {
  if (MOCK_RABBIT_HUNTING) {
    mockRabbitHunting(table);
    return;
  }
  if (MOCK_PRE_BET_INSTEAD_OF_ACTIONS) {
    mockPreBetButtons(table);
    return;
  }
  mockActionButtons(table);
  mockPotButtons(table);
}

function mockRabbitHunting(table: Table) {
  table.betControls.showBetButtons = false;
  table.preBetControls.showPreBet = false;
  table.betControls.showOfferRabbitHunting = true;
  table.betControls.rabbitHuntingPrize = {
    prize: 0.5 * (table.currency?.Multiplier ?? 1),
    currencyId: table.currency?.Id,
    chargingType: 1,
    currency: table.currency,
  };
}

function mockPreBetButtons(table: Table) {
  table.betControls.showBetButtons = false;
  table.preBetControls.showPreBet = true;

  const values = table.preBetControls.preBetValues;
  values[PreBetType.checkFold].visible = true;
  values[PreBetType.check].visible = true;
  values[PreBetType.call].visible = false;
  values[PreBetType.callAny].visible = true;
}

function mockActionButtons(table: Table) {
  const multiplier = table.currency?.Multiplier ?? 1;

  table.betControls.showBetButtons = true;
  table.betControls.showPostBigBlind = false;
  table.betControls.showOfferRabbitHunting = false;
  table.betControls.showReplaceCardsButtons = false;

  table.betControls.showFoldButton = true;
  table.betControls.showCallButton = true;
  table.betControls.showCheckButton = false;
  table.betControls.showRaiseButton = true;

  table.betControls.callButtonText = "page.game.action.call";
  table.betControls.justForShowCallValue = 200 * multiplier;

  table.betControls.raiseButtonText = "page.game.action.raise";
  table.betControls.raiseButtonValue = table.getConvertedAmountText(
    400 * multiplier,
  );
}

function mockTableActionButtons(table: Table) {
  table.actionControls.showBuyRebuyChipsButton = true;
  table.actionControls.checkSeatOutNextHand = true;
  table.handTypeText = "Pair, 8s";

  if (!table.previousHandNumber) {
    table.previousHandNumber = 1;
    table.currentHandNumber = 2;
  }
}

function mockPotButtons(table: Table) {
  table.betControls.showSlider = true;

  table.betControls.btn1Text = "1/3 Pot";
  table.betControls.btn2Text = "1/2 Pot";
  table.betControls.btn3Text = "2/3 Pot";
  table.betControls.btn4Text = "All-In";
}

function mockDealerButtonsAtEverySeat(table: Table, maxPlayers: number) {
  const texture = table.assetsLoader.textures["dealer"].texture;
  for (let i = 0; i < maxPlayers; i++) {
    const pos = table.dealerChipPositions[i];
    if (!pos) {
      continue;
    }
    const layout = table.getDealerButtonLayout(pos);
    const sprite = new PIXI.Sprite(texture);
    sprite.anchor.set(0);
    sprite.scale.set(layout.scaleX, layout.scaleY);
    sprite.position.set(layout.x, layout.y);
    table.container.addChild(sprite);
  }
}
